import { Alert, ProviderConsumer as FluentUIThemeConsumer, Text, ChevronDownIcon, Input, Button, AddIcon, Flex } from '@fluentui/react-northstar';
import { TikitPeoplePicker } from '../../../shared/components/PeoplePicker/TikitPeoplePicker';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appState, AppStateValue } from '../../../AppState';
import { getCachedFeature } from '../../../shared/cache/FeatureCache';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';
import { FilterAccordionValues, FilterPropsItems, getSearchedValues } from '../../../shared/components/Filter';
import { LabelElement } from '../../../shared/components/TicketForm';
import { FILTERS, FilterDataType, LICENSE_FEATURE, ScopeTokens } from '../../../shared/utils/constants';
import { CustomViewContext } from '../CustomViewContextProvider';
import ToolbarCSS from '../Toolbar.module.css';
import { CustomViewFilter } from './CustomViewFilter/CustomViewFilter';
import { getChildFiltersCountFromStorage, getViewActiveFiltersStorageKey, inValidAffectedUsers, inValidRequesters, isChildFilterSelectedExceptQueryValue } from './heplers/customPopupHelper';
import { getDropdownBoxShadow } from '../../ticketHelper';

interface Props {
  defaultFilters: FilterPropsItems[];
  selectedRequesters: PeoplePickerUserType[];
  setSelectedRequesters: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  invalidRequesterMsg: string;
  setInvalidRequesterMsg: React.Dispatch<React.SetStateAction<string>>;
  selectedAffectedUsers: PeoplePickerUserType[];
  setSelectedAffectedUsers: React.Dispatch<React.SetStateAction<PeoplePickerUserType[]>>;
  invalidAffectedUserMsg: string;
  setInvalidAffectedUserMsg: React.Dispatch<React.SetStateAction<string>>;
  filtersByFeature: (item: any, field: string) => void;
  currentAppState: AppStateValue;
}

const Filters = ({
  defaultFilters,
  selectedRequesters,
  setSelectedRequesters,
  invalidRequesterMsg,
  setInvalidRequesterMsg,
  selectedAffectedUsers,
  setSelectedAffectedUsers,
  invalidAffectedUserMsg,
  setInvalidAffectedUserMsg,
  filtersByFeature,
  currentAppState
}: Props) => {
  const state = appState();

  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const { filters } = customViewStates;
  const [localFilters, setLocalFilters] = useState([] as FilterPropsItems[])
  const [visibleFilterIds, setVisibleFilterIds] = useState<number[]>([]);
  const [addFieldSearchValue, setAddFieldSearchValue] = useState('');
  const [isShowAddFieldButton, setIsShowAddFieldButton] = useState(true);
  const [isShowAddFieldDropdown, setIsShowAddFieldDropdown] = useState(false);
  const addFieldWrapperRef = useRef<HTMLDivElement>(null);
  const addFieldSearchRef = useRef<HTMLElement>(null);
  const [searchValues, setSearchValues] = useState<Record<number, string>>({});
  const featureRestrictFilters = filters.filter(item => filtersByFeature(item, "title"));
  const { t } = useTranslation();
  const VIEW_ACTIVE_FILTERS_STORAGE_KEY = getViewActiveFiltersStorageKey(customViewStates.view === 'edit' ? customViewStates.selectedCustomView?.Id : undefined);
  const getStoredFilterOrderIds = (): number[] => {
    if (typeof window === 'undefined') {
      return [];
    }

    const storedValue = localStorage.getItem(VIEW_ACTIVE_FILTERS_STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
      .filter((id, index, arr) => arr.indexOf(id) === index);
  };
  const storedFilterOrderIds = getStoredFilterOrderIds();
  const getFilterSortOrderByStorage = (a: FilterPropsItems, b: FilterPropsItems) => {
    const aIndex = storedFilterOrderIds.indexOf(a.id);
    const bIndex = storedFilterOrderIds.indexOf(b.id);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1) {
      return -1;
    }

    if (bIndex !== -1) {
      return 1;
    }

    return a.sortOrder - b.sortOrder;
  };
  const requesterDetailFilterIds = [
    FILTERS.JobTitle.id,
    FILTERS.Department.id,
    FILTERS.CompanyName.id,
    FILTERS.EmployeeId.id,
    FILTERS.StreetAddress.id,
    FILTERS.State.id,
    FILTERS.Country.id,
    FILTERS.Office.id,
    FILTERS.City.id,
    FILTERS.ZipCode.id,
    FILTERS.OfficePhone.id,
    FILTERS.MobilePhone.id,
    FILTERS.IsExternalUser.id,
    FILTERS.Source.id
  ];
  const hiddenFilterIds = [FILTERS.REQUESTERS.id, FILTERS.AffectedUsers.id, ...requesterDetailFilterIds];
  const nonPickerFilters = localFilters
    .filter((filter) => ![FILTERS.REQUESTERS.id, FILTERS.AffectedUsers.id].includes(filter.id))
    .sort(getFilterSortOrderByStorage);

  const mainFilters = localFilters
    .filter((filter) => !hiddenFilterIds.includes(filter.id))
    .sort(getFilterSortOrderByStorage);

  const visibleFilters = visibleFilterIds
    .map((id) => nonPickerFilters.find((filter) => filter.id === id))
    .filter((filter): filter is FilterPropsItems => Boolean(filter));

  const addableFilters = nonPickerFilters.filter((filter) => {
    return !visibleFilterIds.includes(filter.id);
  });
  const normalizedAddFieldSearchValue = addFieldSearchValue.toLowerCase().replace(/\s+/g, '');
  const filteredAddableFilters = addableFilters.filter((filter) =>
    filter.title.toLowerCase().replace(/\s+/g, '').includes(normalizedAddFieldSearchValue)
  );
  const canShowAddFieldControl = visibleFilters.length > 0 || addableFilters.length > 0;

  const addFilterToView = (filterIdToAdd: number) => {
    if (filterIdToAdd < 0) return;
    setVisibleFilterIds((prev) => (prev.includes(filterIdToAdd) ? prev : [...prev, filterIdToAdd]));
  };

  const resetFilterValues = (filter: FilterPropsItems): FilterPropsItems => {
    const updatedFilter = { ...filter };

    if (updatedFilter.FilterDataType === FilterDataType.String) {
      updatedFilter.stringValue = '';
    }

    if (updatedFilter.values) {
      updatedFilter.values = updatedFilter.values.map((value) => {
        const updatedValue = { ...value, selected: false };
        delete updatedValue.date;
        delete updatedValue.number;
        return updatedValue;
      });
    }

    return updatedFilter;
  };

  const removeFilterFromView = (filterIdToRemove: number) => {
    setVisibleFilterIds((prev) => prev.filter((id) => id !== filterIdToRemove));

    setLocalFilters((prev) => prev.map((filter) => {
      if (filter.id !== filterIdToRemove) return filter;
      return resetFilterValues(filter);
    }));

    setCustomViewStates((prev): CustomViewStatesType => ({
      ...prev,
      filters: prev.filters.map((filter) => {
        if (filter.id !== filterIdToRemove) return filter;
        return resetFilterValues(filter);
      })
    }));

    if (filterIdToRemove === FILTERS.REQUESTERS.id) {
      setSelectedRequesters([]);
      setInvalidRequesterMsg('');
    }

    if (filterIdToRemove === FILTERS.AffectedUsers.id) {
      setSelectedAffectedUsers([]);
      setInvalidAffectedUserMsg('');
    }
  };

  const onAddFieldSearchChange = (value: string) => {
    setAddFieldSearchValue(value);
  };

  const onSelectAddableFilter = (filter: FilterPropsItems) => {
    addFilterToView(filter.id);
    setAddFieldSearchValue('');
    setIsShowAddFieldDropdown(false);
    setIsShowAddFieldButton(true);
  };

  const onClickAddFieldDropdown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setIsShowAddFieldDropdown((prev) => !prev);
  };

  const onClickSelectedValues = (filter: FilterPropsItems, clickedValue: FilterAccordionValues, date?: any) => {
    const isSearchDropdown = filter.id === FILTERS.ASSIGNEE.id;
    const selectedFilterIdx = filters.findIndex((f) => f.id === filter.id);
    const selectedFilter = filters[selectedFilterIdx];
    const selectedValue = selectedFilter.values.find((value) => value.key === clickedValue.key);

    if (filter.FilterDataType == FilterDataType.Boolean) selectedFilter.values.forEach(i => {
      if (i.key != clickedValue.key) i.selected = false;
    });

    if (filter.id == FILTERS.CREATED_DATE.id || filter.id == FILTERS.MODIFIED_DATE.id || filter.FilterDataType == FilterDataType.Date || filter.FilterDataType == FilterDataType.Time || filter.FilterDataType == FilterDataType.Number) {
      //checking if there is a field selected then limiting selection on field dropdown
      const customValueOperatorIsAlreadySelected = filter?.values?.find(x => x.selected);
      const propKey: string = filter.FilterDataType == FilterDataType.Number ? "number" : "date";
      let temp = selectedFilter.values
      .map((value) => {
        if (value.key === clickedValue.key) {
          value.selected = !value.selected;
          value[propKey] = customValueOperatorIsAlreadySelected ? customValueOperatorIsAlreadySelected[propKey] : null;
        }
        else {
          value.selected = false;
          delete value[propKey];
        }
        return value;
      });
      selectedFilter.values = temp;
    }
    else {
      selectedValue.selected = !selectedValue.selected;
    }
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(filters)) }));

    let dropdownValues = [];
    if (isSearchDropdown) {
      dropdownValues = getSearchedValues([...selectedFilter.values], searchValues[filter.id] ?? '');
    } else {
      dropdownValues = selectedFilter.values;
    }

    const localSelectedFilter = localFilters[selectedFilterIdx];
    localSelectedFilter.values = dropdownValues;

    setLocalFilters([...localFilters]);
  };

  const onChangeSelectedValue = (clickedParentId: number, clickedValue: string, propertyKey: string) => {
    const selectedFilterIdx = filters.findIndex((filter) => filter.id === clickedParentId);
    const selectedFilter = filters[selectedFilterIdx];
    const selectedValue = selectedFilter.values.find((value) => value.selected);
    selectedValue[propertyKey] = clickedValue;
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(filters)) }));
    setLocalFilters(filters);
  }
  interface IGetFilterCountReturn {
    [key: string]: number
  }
  const getFilterCount = useCallback((filtersArr: FilterPropsItems[] = JSON.parse(JSON.stringify(filters))): IGetFilterCountReturn => {
    let totalSelectedCount = 0;

    const deleteFilterIfExists = (id: number) => {
      // find index of filter from filters array
      const userFilterIdx = filtersArr.findIndex((filter: any) => filter.id === id);
      // delete filter from filters array, if exists
      if (userFilterIdx !== -1) {
        filtersArr.splice(userFilterIdx, 1);
      }
    }

    deleteFilterIfExists(FILTERS.REQUESTERS.id);
    deleteFilterIfExists(FILTERS.AffectedUsers.id);

    const selectedCount = filtersArr?.reduce((acc, filter) => {
      const selectedValueCount = filter?.FilterDataType === FilterDataType.String
        ? (filter?.stringValue?.trim() ? 1 : 0)
        : filter?.values?.reduce((acc: any, value: any) => {
            acc = value.selected ? acc + 1 : acc;
            return acc;
          }, 0);

      totalSelectedCount += selectedValueCount;

      return { ...acc, [filter.id]: selectedValueCount };
    }, {});

    selectedCount['total'] = totalSelectedCount + selectedRequesters.length + selectedAffectedUsers.length;

    return selectedCount;
  }, [filters, selectedRequesters, selectedAffectedUsers]);

  useEffect(() => {
    (async () => {
      const enabledTicketTags = await getCachedFeature(LICENSE_FEATURE.TicketTags);
      if (!enabledTicketTags) defaultFilters = defaultFilters.filter(x => x.key !== 'Tags');
      if (defaultFilters && customViewStates.filters?.length === 0) {
        setCustomViewStates((prev): CustomViewStatesType => ({
          ...prev, filters: [...JSON.parse(JSON.stringify(defaultFilters))],
        }));
      }
      if (customViewStates.view !== 'edit' && defaultFilters && localFilters.length === 0) {
        setLocalFilters([...JSON.parse(JSON.stringify(customViewStates.filters))]);
      }

      if (customViewStates.view === 'edit') {
        const tempLocalFilters = [...JSON.parse(JSON.stringify(featureRestrictFilters))]
        for (const indx of [FILTERS.ASSIGNEE.id, FILTERS.TicketCollaborators.id]) {
          const selectedFilterIdx = tempLocalFilters.findIndex((filter) =>  filter.id === indx);
          const localSelectedFilter = tempLocalFilters[selectedFilterIdx]
          const dropdownValues = getSearchedValues([...localSelectedFilter.values], '')
          localSelectedFilter.values = dropdownValues
        }

        setSearchValues({})
        setLocalFilters([...tempLocalFilters])
      }

      if (
        isChildFilterSelectedExceptQueryValue(
          await getChildFiltersCountFromStorage(),
          state.filterDropdown?.queryValueApplied
        ) && customViewStates.filters?.length > 0
      ) {
        setLocalFilters(JSON.parse(JSON.stringify(customViewStates.filters)))
      }

    })()

  }, [defaultFilters]);

  useEffect(() => {
    if (nonPickerFilters.length === 0) {
      setVisibleFilterIds([]);
      return;
    }

    setVisibleFilterIds((prev) => {
      const validPrevIds = prev.filter((id) => nonPickerFilters.some((filter) => filter.id === id));
      if (validPrevIds.length > 0) {
        return validPrevIds;
      }

      const validStoredVisibleFilterIds = storedFilterOrderIds
        .filter((id, index) => storedFilterOrderIds.indexOf(id) === index)
        .filter((id) => nonPickerFilters.some((filter) => filter.id === id));
      if (validStoredVisibleFilterIds.length > 0) {
        return validStoredVisibleFilterIds;
      }

      const defaultVisibleMainIds = [
        FILTERS.CATEGORY.id,
        FILTERS.TICKET_TYPE.id,
        FILTERS.STATUS.id,
        FILTERS.PRIORITY.id,
        FILTERS.TEAM.id,
        FILTERS.GROUP.id,
        FILTERS.ASSIGNEE.id
      ].filter((id): id is number => typeof id === 'number' && mainFilters.some((filter) => filter.id === id));
      const preselectedFilterIds = nonPickerFilters
        .filter((filter) =>
          (filter.FilterDataType === FilterDataType.String && !!filter.stringValue?.trim()) ||
          filter.values?.some((value) => value.selected)
        )
        .map((filter) => filter.id);

      return [
        ...defaultVisibleMainIds,
        ...preselectedFilterIds.filter((id) => !defaultVisibleMainIds.includes(id))
      ];
    });

  }, [localFilters, customViewStates.view]);

  useEffect(() => {
    if (nonPickerFilters.length === 0 || typeof window === 'undefined') {
      return;
    }

    const validVisibleFilterIds = visibleFilterIds.filter((id) =>
      nonPickerFilters.some((filter) => filter.id === id)
    );

    try {
      localStorage.setItem(VIEW_ACTIVE_FILTERS_STORAGE_KEY, JSON.stringify(validVisibleFilterIds));
    } catch (_error) {
      // no-op when browser storage is unavailable
    }
  }, [visibleFilterIds, nonPickerFilters]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (addFieldWrapperRef.current && !addFieldWrapperRef.current.contains(event.target) && isShowAddFieldDropdown) {
        setIsShowAddFieldDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isShowAddFieldDropdown]);

  useEffect(() => {
    // preventScroll: a plain focus can scroll the overflow-hidden app shell behind the dialog.
    if (isShowAddFieldDropdown) addFieldSearchRef.current?.focus({ preventScroll: true });
  }, [isShowAddFieldDropdown]);

  const defaultSelectedUsersFromFilterValues = async (filteredValues: null | FilterAccordionValues[]) => {
    if (!filteredValues) return [];
    const users = await getCachedUsers(filteredValues.map(x => x.key));
    let tempList = users.map(u => ({
      displayName: u.FullName,
      userPrincipalName: u.Email,
      givenName: u.FullName,
      surname: u.FamilyName,
      mail: u.Email,
      id: u.AadObjectId
    }));

    if (filteredValues.some(x=>x.key==-1))
      tempList.unshift({ ...ScopeTokens.MeTokenData, displayName: t('common.me') });

    return tempList;
  }

  // set initial selectedRequesters in case of custom_view editing or saving(when customViewStates.view is equal to 'edit' or 'save')
  useEffect(() => {
    if (customViewStates.view !== 'edit' && customViewStates.view !== 'save') {
      setSelectedRequesters([]);
      setSelectedAffectedUsers([])
      return;
    }

    if (customViewStates.view === 'edit' || customViewStates.view === 'save') {
      const setPickers = async () => {
        let requesterValues = customViewStates.filters.find((filteredItem) => filteredItem.id === FILTERS.REQUESTERS.id);
        if (requesterValues?.values && requesterValues?.values?.length !== 0) {
          let filteredRequesterValues = requesterValues.values.filter((item) => item.selected);
          const requesters = await defaultSelectedUsersFromFilterValues(filteredRequesterValues);
          setSelectedRequesters(requesters);
        }

        let affectedUserValues = customViewStates.filters.find((filteredItem) => filteredItem.id === FILTERS.AffectedUsers.id);
        if (affectedUserValues?.values && affectedUserValues?.values?.length !== 0) {
          let filteredaffectedUserValues = affectedUserValues.values.filter((item) => item.selected);
          const selectedAffectedUser = await defaultSelectedUsersFromFilterValues(filteredaffectedUserValues);
          setSelectedAffectedUsers(selectedAffectedUser);
        }
      };
      
      setPickers();
    };
  }, []);

  const mapPeoplePickerUserTypeToPlatformUser = (user: PeoplePickerUserType): PlatformUser => ({
    UserName: user.userPrincipalName || user.mail || user.displayName,
    GivenName: user.givenName || '',
    FamilyName: user.surname || '',
    Salutation: null,
    Email: user.mail || '',
    PhoneNumber: null,
    HomePhone: null,
    MobilePhone: null,
    Id: Number(user.id) || 0,
    ModifiedDate: '',
    ModifiedById: null,
    CreatedDate: '',
    CreatedById: null,
    Guid: '',
    IsDeleted: false,
    FullName: user.displayName || '',
    AadObjectId: user.id,
    ServiceUrl: '',
    TeamsUserId: '',
    HelpTeamsUserId: '',
    TeamsTimeZoneName: '',
    HasApproved: 0,
    UserPrincipalName: user.userPrincipalName,
    TVATeamsUserId: '',
    Type: null,
    Notes: null,
    AdminMode: '',
    JobTitle: '',
    IsExternalUser: false,
    Source: 0
  });

  const mapSelectedUsersToPlatformUsers = (users: PeoplePickerUserType[]) => users.map(mapPeoplePickerUserTypeToPlatformUser);

  const onChangeSearchDropdown = (e: any) => {
    const value: string = e.target.value;
    setSearchValues(prev => ({ ...prev, [FILTERS.ASSIGNEE.id]: value }))

    const filterIdx = filters.findIndex(filter => filter.title === FILTERS.ASSIGNEE.title)
    const values = filters[filterIdx].values
    const dropdownValues = getSearchedValues([...values], value)

    localFilters[filterIdx].values = dropdownValues

    setLocalFilters((_prev) => ([...localFilters]))
  }

  const onChangeCollaboratorSearchDropdown = (e: any) => {
    const value: string = e.target.value;
    setSearchValues(prev => ({ ...prev, [FILTERS.TicketCollaborators.id]: value }))

    const filterIdx = filters.findIndex(filter => filter.title === FILTERS.TicketCollaborators.title)
    const values = filters[filterIdx].values
    const dropdownValues = getSearchedValues([...values], value)

    localFilters[filterIdx].values = dropdownValues

    setLocalFilters((_prev) => ([...localFilters]))
  }

  const onTextChange = (e: any, filterId: number) => {
    const value: string = e.value;
    const filterIdx = filters.findIndex(filter => filter.id === filterId)
    localFilters[filterIdx].stringValue = value;
    setLocalFilters((_prev) => ([...localFilters]))
    setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, filters: JSON.parse(JSON.stringify(localFilters)) }));
  }

  return (
    <FluentUIThemeConsumer
      render={(globalTheme) => {
        return (
          <div style={{ height: '100%' }} >
            <div className='font-semibold mt-3'>
              Filters{' '}{getFilterCount()['total'] > 0 ? `(${getFilterCount()['total']})` : ``}
            </div>
            {invalidRequesterMsg && (
              <Alert
                fitted
                danger={true}
                content={invalidRequesterMsg}
                style={{
                  fontSize: 12,
                  width: '100%',
                  marginBottom: '.5rem',
                  marginTop: '1rem',
                }}
              />
            )}
            <div className='flex flex-col mt-2'>
              <div className='flex flex-wrap gap-2 mt-2'>
                <div className={`${ToolbarCSS.filterItem} relative`}>
                  <div>
                    <LabelElement label={`${t('ticket.ticket-list.configure-column.requesters')} ${selectedRequesters.length > 0 ? '(' + selectedRequesters.length + ')' : ''}`}>
                      <Flex style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}>
                      <TikitPeoplePicker
                        options={currentAppState?.usersInRole || []}
                        value={mapSelectedUsersToPlatformUsers(selectedRequesters)}
                        onChange={(selected: PlatformUser[]) => {
                          const detail = selected.map(u => ({
                            displayName: u.FullName || u.UserName,
                            mail: u.Email,
                            givenName: u.GivenName || '',
                            surname: u.FamilyName || '',
                            userPrincipalName: u.UserPrincipalName || u.UserName,
                            id: u.AadObjectId || String(u.Id)
                          }));
                          inValidRequesters(detail, setInvalidRequesterMsg);
                          setSelectedRequesters([...detail]);
                        }}
                        isMultiple
                        globalTheme={globalTheme}
                        menuPortalBehavior='overlay'
                        enableMeToken={true}
                      />
                      </Flex>
                    </LabelElement>
                  </div>
                </div>
                <div className={`${ToolbarCSS.filterItem} relative`}>
                  <div>
                    <LabelElement label={`${t('ticket.ticket-list.configure-column.affected-users')} ${selectedAffectedUsers.length > 0 ? '(' + selectedAffectedUsers.length + ')' : ''}`}></LabelElement>
                    <Flex style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}>
                    <TikitPeoplePicker
                      options={currentAppState?.usersInRole || []}
                      value={mapSelectedUsersToPlatformUsers(selectedAffectedUsers)}
                      onChange={(selected: PlatformUser[]) => {
                        const detail = selected.map(u => ({
                          displayName: u.FullName || u.UserName,
                          mail: u.Email,
                          givenName: u.GivenName || '',
                          surname: u.FamilyName || '',
                          userPrincipalName: u.UserPrincipalName || u.UserName,
                          id: u.AadObjectId || String(u.Id)
                        }));
                        inValidAffectedUsers(detail, setInvalidAffectedUserMsg);
                        setSelectedAffectedUsers([...detail]);
                      }}
                      isMultiple
                      globalTheme={globalTheme}
                      menuPortalBehavior='overlay'
                      enableMeToken={true}
                    />
                    </Flex>
                  </div>
                </div>
              </div>
              <div className='grid grid-cols-3 gap-2 mt-2'>
                {visibleFilters
                  .map((filter: FilterPropsItems) => {
                    return (
                      <Flex key={filter.id}>
                        <CustomViewFilter
                          filter={filter}
                          searchValue={searchValues[filter.id] ?? ''}
                          globalTheme={globalTheme}
                          getFilterCount={getFilterCount}
                          onChangeSearchDropdown={onChangeSearchDropdown}
                          onClick={(value, date) => onClickSelectedValues(filter, value, date)}
                          onChangeCollaboratorSearchDropdown={onChangeCollaboratorSearchDropdown}
                          onChangeSelectedValue={(value, propKey) => onChangeSelectedValue(filter.id, value, propKey)}
                          onTextChange={onTextChange}
                          onRemoveFilterFromView={() => removeFilterFromView(filter.id)}
                        />
                      </Flex>
                    )
                  })}
                {canShowAddFieldControl && (<>
                  {isShowAddFieldButton ? (
                    <div><Button
                      icon={<AddIcon styles={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }} />}
                      text
                      primary
                      content={<span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}>{t('automation.automation-group.add-filter')}</span>}
                      onClick={() => {
                        setIsShowAddFieldButton(false);
                        setIsShowAddFieldDropdown(true);
                      }}
                    /></div>
                  ) : (
                  <div ref={addFieldWrapperRef} className={`${ToolbarCSS.filterItem} relative`}>
                    <div
                      onClick={(e) => onClickAddFieldDropdown(e)}
                      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                      className={`flex justify-between px-3 py-2 cursor-pointer rounded-sm ${ToolbarCSS['custom-view-dropdown']}`}
                    >
                      <>
                        <Text
                          style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}
                          content={<span>{t('automation.automation-group.add-filter')}</span>}
                        />
                        <div className='relative'>
                          <div className='absolute top-0 right-0 h-full p-2 z-10'></div>
                          <ChevronDownIcon
                            style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}
                            className={`${ToolbarCSS.filterOptionArrow} ${isShowAddFieldDropdown && ToolbarCSS.filterOptionHidden}`}
                          />
                        </div>
                      </>
                    </div>

                    {isShowAddFieldDropdown && (
                      <div
                        style={{ boxShadow: `${getDropdownBoxShadow(globalTheme)}`, backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
                        className='origin-top-right min-w-full absolute left-0 rounded-md focus:outline-none max-h-72 overflow-y-scroll z-10 my-2'
                        role='menu'
                        aria-orientation='vertical'
                        aria-labelledby='menu-button'
                        tabIndex={-1}
                      >
                        <Input
                          fluid
                          className={ToolbarCSS.filterSearch}
                          style={{
                            backgroundColor: globalTheme.siteVariables.colorScheme.default.background1,
                            padding: '2px 0px',
                            marginTop: '2px',
                          }}
                          value={addFieldSearchValue}
                          inputRef={addFieldSearchRef}
                          onChange={(_e, data) => onAddFieldSearchChange(String(data?.value || ''))}
                          inverted
                          placeholder='Search fields...'
                        />
                        {filteredAddableFilters.map((filter) => (
                          <div
                            key={filter.id}
                            className='px-3 py-2 cursor-pointer'
                            onClick={() => onSelectAddableFilter(filter)}
                          >
                            <Text
                              style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}
                              content={filter.title}
                            />
                          </div>
                        ))}
                        {filteredAddableFilters.length === 0 && (
                          <div className='px-3 py-2 opacity-70'>No fields found</div>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </>)}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

export default Filters;
